import isEmpty from 'lodash/isEmpty';
import { FC, useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { formatEditorValueFromJsObj } from 'modules/aceEditor';
import { JsonTestData } from 'modules/graphql';
import { addNegativeToast } from 'modules/toastContainer';
import { isValidJson } from 'utils/stringHelpers';

import { displayContent } from '../common/content';
import { UserProfileDataType } from '../common/types';
import { getDataFeedData, getUserData } from '../common/utils';
import { useEditableFieldsContext } from './EditableFieldsContext';
import { PreviewWithDataContext, PreviewWithDataContextState } from './PreviewWithDataContext';
import { useQueryParamContext } from './QueryParamContext';

export const PreviewWithDataContextProvider: FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const { templateId } = useQueryParamContext();
  const { liveTemplate } = useEditableFieldsContext();
  const { dataFeedIds, jsonTestData } = liveTemplate;
  const dispatch = useDispatch();
  const [isLoadingDataFeeds, setIsLoadingDataFeeds] = useState<boolean>(false);
  const [isLoadingUserData, setIsLoadingUserData] = useState<boolean>(false);
  const [isUserDataDirty, setIsUserDataDirty] = useState<boolean>(false);
  const [dataFeedData, setDataFeedData] = useState<string>(
    jsonTestData ? formatEditorValueFromJsObj(jsonTestData.dataFeedJson) : '{}',
  );
  const [userData, setUserData] = useState<string>(
    jsonTestData ? formatEditorValueFromJsObj(jsonTestData.userJson) : '{}',
  );
  const dataFeedDataIsValid = isValidJson(dataFeedData);
  const userDataIsValid = !!userData && isValidJson(userData);
  const isDataFeedDataEmpty = dataFeedDataIsValid
    ? isEmpty(JSON.parse(dataFeedData))
    : dataFeedData.length < 3;
  const isUserDataEmpty = userDataIsValid
    ? isEmpty(JSON.parse(userData))
    : (userData?.length ?? 0) < 3;
  const onLoadDataFeeds = useCallback(() => {
    setIsLoadingDataFeeds(true);
    getDataFeedData(templateId, JSON.parse(userData || '{}'))
      .then(({ data }) => {
        setDataFeedData(formatEditorValueFromJsObj(data.contents));
      })
      .catch((error: any) => {
        dispatch(addNegativeToast(displayContent.errDataFeed, error));
      })
      .finally(() => {
        setIsLoadingDataFeeds(false);
      });
  }, [templateId, userData]);

  const onLoadUserData = useCallback((id: string, userDataType: UserProfileDataType) => {
    setIsLoadingUserData(true);
    getUserData(id, userDataType)
      .then(({ data }) => {
        setUserData(formatEditorValueFromJsObj(data));
      })
      .catch((error: any) => {
        dispatch(addNegativeToast(displayContent.errUserData, error));
      })
      .finally(() => {
        setIsLoadingUserData(false);
      });
  }, []);
  /** Data feeds should auto load when preview page is rendered
   * and update when new data feeds have been added */
  useEffect(() => {
    if (dataFeedIds?.length) {
      onLoadDataFeeds();
    }
  }, [dataFeedIds]);

  /**
   * Load in json test data when the value is received
   * This should be pretty fast but eventually we'll want to add some loading state
   * to user changes being overwritten when the jsonTestData loads
   */
  useEffect(() => {
    if (jsonTestData) {
      const { dataFeedJson, userJson } = jsonTestData as JsonTestData;
      if (dataFeedJson) {
        setDataFeedData(formatEditorValueFromJsObj(dataFeedJson));
      }
      if (userJson) {
        setUserData(formatEditorValueFromJsObj(userJson));
      }
    }
  }, [!!jsonTestData]);

  const context: PreviewWithDataContextState = {
    canRefreshPreview: dataFeedDataIsValid && userDataIsValid,
    dataFeedData,
    dataFeedDataIsValid,
    isDataFeedDataEmpty,
    isLoadingDataFeeds,
    isLoadingUserData,
    isUserDataEmpty,
    onLoadDataFeeds,
    onLoadUserData,
    setDataFeedData,
    setUserData,
    userData,
    userDataIsValid,
    isUserDataDirty,
    setIsUserDataDirty,
  };

  return (
    <PreviewWithDataContext.Provider value={context}>{children}</PreviewWithDataContext.Provider>
  );
};
